import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {EligibleOfferSetModel} from '../../../../../model/eligible-offer-set.model';
import {MakeModel} from '../../../../../model/make.model';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {VehicleModel} from '../../../../../model/vehicle-model.model';
import {VehicleService} from '../../../../../service/vehicle.service';

const offerSetRuleType = 'offerSetRule';
const discountRuleType = 'discountRule';
const activeStatus = 'Active';
const inactiveStatus = 'Inactive';
const unavailableOfferSetSuffix = '(Unavailable)';
const unavailableOfferSetErrorId = 'generated-offer-set-unavailable';

@Component({
  selector: 'auto-ipop-explicit-save-offer-set-rule-modal',
  templateUrl: './ipop-explicit-save-offer-set-rule-modal.component.html',
  styleUrls: ['./ipop-explicit-save-offer-set-rule-modal.component.scss'],
  standalone: false
})
export class IpopExplicitSaveOfferSetRuleModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() dialogTitle = '';
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() rule: OfferRuleModel | null = null;
  @Input() isGeneratedPrefill = false;
  @Input() selectedRuleType = offerSetRuleType;
  @Input() eligibleOfferSets: EligibleOfferSetModel[] = [];
  @Input() offerSetOfferTextMap: {[key: number]: string} = {};
  @Input() areActionsLocked = false;

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<OfferRuleModel>();

  ruleForm: FormGroup;
  ruleTypeOptions = [
    {label: 'Offer Set Rule', value: offerSetRuleType},
    {label: 'Dollar Discount Rule', value: discountRuleType}
  ];
  filteredRuleTypeOptions = [...this.ruleTypeOptions];
  publicOfferSetTooltip = 'Dollar discount rules are applicable to private offer sets';
  privacyOptions = [
    {label: 'Public', value: false},
    {label: 'Private', value: true}
  ];
  conditionOptions = [
    {label: 'New', value: 'New'},
    {label: 'Used', value: 'Used'},
    {label: 'CPO', value: 'CPO'},
    {label: 'Any', value: 'Any'}
  ];
  makes: MakeModel[] = [];
  models: VehicleModel[] = [];
  filteredModels: VehicleModel[] = [];
  filteredEligibleOfferSets: EligibleOfferSetModel[] = [];
  anyMakeOption: MakeModel;
  anyModelOption: VehicleModel;
  selectedMakeId: number | null = null;
  generatedOfferSetId: number | null = null;
  generatedOfferSetUnavailable = false;

  constructor(
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private vehicleService: VehicleService
  ) {
  }

  ngOnInit(): void
  {
    this.initializeForm();
    this.initializeAnyOptions();
    this.loadMakes();
    this.loadModels();
    this.updateRuleTypeOptions();
    this.updateFilteredOfferSets();
    this.applyActionLockState();
  }

  /**
   * Reapplies modal state when visibility, rule, Offer Sets, or locks change.
   */
  ngOnChanges(changes: SimpleChanges): void
  {
    const visibilityChange = changes['visible'];
    const ruleChange = changes['rule'];
    const generatedPrefillChange = changes['isGeneratedPrefill'];
    const offerSetChange = changes['eligibleOfferSets'];
    if (visibilityChange) {
      this.handleVisibilityChange();
    }
    if ((ruleChange || generatedPrefillChange) && this.visible && this.ruleForm) {
      this.applyRuleToForm();
    }
    if (offerSetChange) {
      this.updateGeneratedOfferSetAvailability();
      this.updateFilteredOfferSets();
    }
    if (this.ruleForm) {
      this.applyActionLockState();
    }
  }

  /**
   * Applies validators and control state for the selected rule type.
   */
  onRuleTypeChange(): void {
    const isDiscountRule = this.isDiscountRule();
    const offerSetControl = this.ruleForm.get('offerSetId');
    const discountControl = this.ruleForm.get('dollarAmountDiscount');
    if (offerSetControl) {
      this.configureOfferSetControl(offerSetControl, isDiscountRule);
    }
    if (discountControl) {
      this.configureDiscountControl(discountControl, isDiscountRule);
    }
  }

  /**
   * Prefills rule criteria from the selected eligible Offer Set.
   */
  onOfferSetChange(offerSetId: number | null): void {
    const selectedOfferSet = this.eligibleOfferSets.find((offerSet) => offerSet.id === offerSetId);
    if (selectedOfferSet) {
      this.ruleForm.patchValue(this.buildOfferSetPatch(selectedOfferSet));
      this.applyMakeAndModelSelectionFromForm();
    }
    this.updateGeneratedOfferSetAvailability();
    this.updateFilteredOfferSets();
  }

  /**
   * Reapplies rule and Offer Set restrictions after privacy changes.
   */
  onPrivacySelectionChange(): void {
    this.updateRuleTypeOptions();
    this.updateGeneratedOfferSetAvailability();
    this.updateFilteredOfferSets();
    this.applyPublicOfferSetRestrictions();
    this.onRuleTypeChange();
  }

  /**
   * Updates model options and form values for the selected make.
   */
  onMakeSelectionChange(makeId: number | null): void {
    this.selectedMakeId = makeId;
    this.ruleForm.patchValue({make: this.getMakeNameById(makeId), model: '', modelId: null});
    this.filterModelsForMake(makeId);
  }

  /**
   * Stores the selected model name in the submitted form value.
   */
  onModelSelectionChange(modelId: number | null): void {
    let modelName = '';
    if (modelId !== null) {
      const selectedModel = this.filteredModels.find((model) => model.id === modelId);
      if (selectedModel) {
        modelName = selectedModel.name;
      }
    }
    this.ruleForm.patchValue({model: modelName});
  }

  /**
   * Emits a valid unlocked local rule or reports validation errors.
   */
  submitRule(): void
  {
    const formValid = this.isFormValidForSubmission();
    if (formValid && !this.areActionsLocked) {
      const payload = this.buildPayloadFromForm();
      this.submitted.emit(payload);
    }
    if (!formValid && !this.areActionsLocked) {
      this.ruleForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please correct the highlighted fields before continuing.'
      });
    }
  }

  /**
   * Requests modal closure without submitting local changes.
   */
  closeDialog(): void {
    this.closed.emit();
  }

  /**
   * Checks whether locking or an unavailable generated Offer Set blocks submission.
   */
  getSubmitDisabled(): boolean
  {
    const isDisabled = this.areActionsLocked || this.isGeneratedOfferSetSelectionUnavailable();

    return isDisabled;
  }

  /**
   * Enables or disables form controls to match the external action lock.
   */
  private applyActionLockState(): void
  {
    if (this.areActionsLocked) {
      this.ruleForm.disable({emitEvent: false});
    }
    if (!this.areActionsLocked) {
      this.ruleForm.enable({emitEvent: false});
      this.applyPublicOfferSetRestrictions();
      this.onRuleTypeChange();
    }
  }

  /**
   * Checks whether the selected generated Offer Set is unavailable.
   */
  isGeneratedOfferSetSelectionUnavailable(): boolean {
    const selectedOfferSetId = this.getControlNumber('offerSetId');
    const isUnavailable = this.generatedOfferSetUnavailable && selectedOfferSetId === this.generatedOfferSetId;

    return isUnavailable;
  }

  /**
   * Provides the accessibility error reference for an unavailable Offer Set.
   */
  getGeneratedOfferSetErrorId(): string | null {
    let errorId: string | null = null;
    if (this.isGeneratedOfferSetSelectionUnavailable()) {
      errorId = unavailableOfferSetErrorId;
    }

    return errorId;
  }

  /**
   * Resolves offer text for the currently selected Offer Set.
   */
  getSelectedOfferSetOfferText(): string {
    let offerText = '';
    if (this.ruleForm) {
      const offerSetControl = this.ruleForm.get('offerSetId');
      if (offerSetControl) {
        const offerSetId = this.resolveNumber(offerSetControl.value);
        if (offerSetId !== null) {
          offerText = this.offerSetOfferTextMap[offerSetId] || '';
        }
      }
    }

    return offerText;
  }

  /**
   * Checks whether the selected Offer Set is public.
   */
  isPublicOfferSetSelected(): boolean {
    let isPublic = false;
    if (this.ruleForm) {
      const offerSetControl = this.ruleForm.get('offerSetId');
      if (offerSetControl) {
        const offerSetId = this.resolveNumber(offerSetControl.value);
        const offerSet = this.eligibleOfferSets.find((option) => option.id === offerSetId);
        if (offerSet && offerSet.isPrivate === false) {
          isPublic = true;
        }
      }
    }

    return isPublic;
  }

  /**
   * Checks whether the rule privacy selection is public.
   */
  isPublicRuleSelection(): boolean {
    let isPublic = false;
    if (this.ruleForm) {
      const privacyControl = this.ruleForm.get('isPrivate');
      if (privacyControl) {
        isPublic = privacyControl.value === false;
      }
    }

    return isPublic;
  }

  /**
   * Creates the rule form and its base validators.
   */
  private initializeForm(): void {
    this.ruleForm = this.formBuilder.group({
      ruleType: [offerSetRuleType, Validators.required],
      status: [true, Validators.required],
      isPrivate: [true],
      offerSetId: [null],
      year: [null],
      make: [''],
      makeId: [null],
      model: [''],
      modelId: [{value: null, disabled: true}],
      trim: [''],
      maxSalePrice: [null, Validators.min(0)],
      vehicleLimit: [null, Validators.min(0)],
      vehicleCondition: ['New', Validators.required],
      dollarAmountDiscount: [null, Validators.min(0.01)]
    });
  }

  /**
   * Creates wildcard make and model options.
   */
  private initializeAnyOptions(): void {
    this.anyMakeOption = new MakeModel();
    this.anyMakeOption.id = null;
    this.anyMakeOption.displayName = 'Any';
    this.anyMakeOption.active = true;
    this.anyModelOption = new VehicleModel();
    this.anyModelOption.id = null;
    this.anyModelOption.makeId = null;
    this.anyModelOption.name = 'Any';
    this.anyModelOption.active = true;
  }

  /**
   * Loads available makes and reapplies existing form selections.
   */
  private loadMakes(): void {
    this.vehicleService.getAllMakes().subscribe({
      next: (makes) => {
        this.makes = [this.anyMakeOption, ...makes];
        this.applyMakeAndModelSelectionFromForm();
      },
      error: () => {
        this.showLoadError('Failed to load makes.');
      }
    });
  }

  /**
   * Loads active models and reapplies existing form selections.
   */
  private loadModels(): void {
    this.vehicleService.getAllModels().subscribe({
      next: (models) => {
        this.models = models.filter((model) => model.active);
        this.filteredModels = [this.anyModelOption, ...this.models];
        this.applyMakeAndModelSelectionFromForm();
      },
      error: () => {
        this.showLoadError('Failed to load models.');
      }
    });
  }

  /**
   * Populates an opened modal or resets a closed modal.
   */
  private handleVisibilityChange(): void {
    if (this.visible && this.ruleForm) {
      this.applyRuleToForm();
    }
    if (!this.visible && this.ruleForm) {
      this.ruleForm.reset();
    }
  }

  /**
   * Applies edit, generated-prefill, or default values to the form.
   */
  private applyRuleToForm(): void {
    this.generatedOfferSetId = null;
    this.generatedOfferSetUnavailable = false;
    if (this.mode === 'edit' && this.rule) {
      this.setFormForEdit(this.rule);
    }
    if (this.mode === 'create' && this.isGeneratedPrefill && this.rule) {
      this.setGeneratedFormValues(this.rule);
    }
    if ((this.mode === 'create' && !this.isGeneratedPrefill) || !this.rule) {
      this.setDefaultFormValues();
    }
    this.updateRuleTypeOptions();
    this.updateGeneratedOfferSetAvailability();
    this.updateFilteredOfferSets();
    this.applyPublicOfferSetRestrictions();
    this.onRuleTypeChange();
  }

  /**
   * Resets the form to defaults for a manually created rule.
   */
  private setDefaultFormValues(): void {
    this.ruleForm.setValue({
      ruleType: this.selectedRuleType,
      status: true,
      isPrivate: true,
      offerSetId: null,
      year: null,
      make: '',
      makeId: null,
      model: '',
      modelId: null,
      trim: '',
      maxSalePrice: null,
      vehicleLimit: null,
      vehicleCondition: 'New',
      dollarAmountDiscount: null
    });
    this.selectedMakeId = null;
    this.filterModelsForMake(null);
  }

  /**
   * Tracks the generated Offer Set before applying generated rule values.
   */
  private setGeneratedFormValues(rule: OfferRuleModel): void {
    this.generatedOfferSetId = this.resolveNumber(rule.offerSetId);
    this.setFormForEdit(rule);
  }

  /**
   * Maps an existing or generated rule into editable form values.
   */
  private setFormForEdit(rule: OfferRuleModel): void {
    const makeName = this.resolveString(rule.make);
    const modelName = this.resolveString(rule.model);
    const makeId = this.findMakeIdByName(makeName);
    const modelId = this.findModelIdByNameAndMake(modelName, makeId);
    this.ruleForm.setValue({
      ruleType: this.getRuleTypeFromRule(rule),
      status: rule.status === activeStatus,
      isPrivate: rule.isPrivate === true,
      offerSetId: this.resolveNumber(rule.offerSetId),
      year: this.resolveNumber(rule.year),
      make: makeName,
      makeId,
      model: modelName,
      modelId,
      trim: this.resolveString(rule.trim),
      maxSalePrice: this.resolveNumber(rule.maxSalePrice),
      vehicleLimit: this.resolveNumber(rule.vehicleLimit),
      vehicleCondition: this.normalizeCondition(rule.vehicleCondition),
      dollarAmountDiscount: this.resolveNumber(rule.dollarAmountDiscount)
    });
    this.selectedMakeId = makeId;
    this.filterModelsForMake(makeId);
  }

  /**
   * Verifies form validity and rule-type-specific required values.
   */
  private isFormValidForSubmission(): boolean {
    let isValid = this.ruleForm.valid;
    const discountRule = this.isDiscountRule();
    const offerSetId = this.getControlNumber('offerSetId');
    const discountAmount = this.getControlNumber('dollarAmountDiscount');
    if (discountRule && (discountAmount === null || discountAmount <= 0)) {
      isValid = false;
    }
    if (!discountRule && (offerSetId === null || offerSetId <= 0)) {
      isValid = false;
    }

    return isValid;
  }

  /**
   * Builds a local Offer Rule payload from normalized form values.
   */
  private buildPayloadFromForm(): OfferRuleModel {
    const payload = new OfferRuleModel();
    payload.offerSetId = this.getControlNumber('offerSetId');
    payload.year = this.getControlNumber('year');
    payload.maxSalePrice = this.getControlNumber('maxSalePrice');
    payload.vehicleLimit = this.getControlNumber('vehicleLimit');
    payload.dollarAmountDiscount = this.getControlNumber('dollarAmountDiscount');
    payload.isPrivate = this.getControlBoolean('isPrivate');
    payload.make = this.getControlString('make');
    payload.model = this.getControlString('model');
    payload.trim = this.getControlString('trim');
    payload.vehicleCondition = this.getControlString('vehicleCondition') || 'Any';
    payload.status = inactiveStatus;
    if (this.getControlBoolean('status')) {
      payload.status = activeStatus;
    }

    return payload;
  }

  /**
   * Configures Offer Set validators and availability for the rule type.
   */
  private configureOfferSetControl(control: import('@angular/forms').AbstractControl, isDiscountRule: boolean): void {
    if (isDiscountRule) {
      control.clearValidators();
      control.setValue(null);
      control.disable({emitEvent: false});
    }
    if (!isDiscountRule) {
      control.setValidators([Validators.required, this.getGeneratedOfferSetAvailabilityValidator()]);
      control.enable({emitEvent: false});
    }
    control.updateValueAndValidity({emitEvent: false});
  }

  /**
   * Configures discount validators and availability for the rule type.
   */
  private configureDiscountControl(control: import('@angular/forms').AbstractControl, isDiscountRule: boolean): void {
    if (isDiscountRule) {
      control.setValidators([Validators.required, Validators.min(0.01)]);
      control.enable({emitEvent: false});
    }
    if (!isDiscountRule) {
      control.clearValidators();
      control.setValue(null);
      control.disable({emitEvent: false});
    }
    control.updateValueAndValidity({emitEvent: false});
  }

  /**
   * Prevents public rules from selecting dollar discount behavior.
   */
  private applyPublicOfferSetRestrictions(): void {
    if (this.isPublicRuleSelection()) {
      this.ruleForm.patchValue({ruleType: offerSetRuleType, dollarAmountDiscount: null});
    }
  }

  /**
   * Filters rule types according to the current privacy selection.
   */
  private updateRuleTypeOptions(): void {
    this.filteredRuleTypeOptions = [...this.ruleTypeOptions];
    if (this.isPublicRuleSelection()) {
      this.filteredRuleTypeOptions = this.ruleTypeOptions.filter((option) => option.value === offerSetRuleType);
    }
  }

  /**
   * Filters eligible Offer Sets by privacy and retains unavailable generated values.
   */
  private updateFilteredOfferSets(): void {
    let isPrivate = true;
    if (this.ruleForm) {
      isPrivate = this.getControlBoolean('isPrivate');
    }
    this.filteredEligibleOfferSets = this.eligibleOfferSets.filter((offerSet) => offerSet.isPrivate === isPrivate);
    if (this.generatedOfferSetUnavailable && this.generatedOfferSetId !== null) {
      this.filteredEligibleOfferSets = [this.buildUnavailableGeneratedOfferSet(), ...this.filteredEligibleOfferSets];
    }
  }

  /**
   * Tracks whether the generated Offer Set remains eligible for selection.
   */
  private updateGeneratedOfferSetAvailability(): void {
    this.generatedOfferSetUnavailable = false;
    if (this.isGeneratedPrefill && this.generatedOfferSetId !== null && this.ruleForm) {
      const generatedPrivacy = this.getControlBoolean('isPrivate');
      const matchingOfferSet = this.eligibleOfferSets.find((offerSet) => {
        return offerSet.id === this.generatedOfferSetId && offerSet.isPrivate === generatedPrivacy;
      });
      this.generatedOfferSetUnavailable = matchingOfferSet === undefined;
      const offerSetControl = this.ruleForm.get('offerSetId');
      if (offerSetControl) {
        offerSetControl.updateValueAndValidity({emitEvent: false});
      }
    }
  }

  /**
   * Creates a validator that rejects an unavailable generated Offer Set.
   */
  private getGeneratedOfferSetAvailabilityValidator(): ValidatorFn {
    const validator = (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = null;
      const offerSetId = this.resolveNumber(control.value);
      if (this.generatedOfferSetUnavailable && offerSetId === this.generatedOfferSetId) {
        errors = {generatedOfferSetUnavailable: true};
      }

      return errors;
    };

    return validator;
  }

  /**
   * Builds a disabled display option for an unavailable generated Offer Set.
   */
  private buildUnavailableGeneratedOfferSet(): EligibleOfferSetModel {
    const offerSet = new EligibleOfferSetModel();
    const displayTitle = `Offer Set ${this.generatedOfferSetId} ${unavailableOfferSetSuffix}`;
    offerSet.id = this.generatedOfferSetId ?? undefined;
    offerSet.accountId = this.rule?.accountId;
    offerSet.title = displayTitle;
    offerSet.displayTitle = displayTitle;
    offerSet.isPrivate = this.getControlBoolean('isPrivate');

    return offerSet;
  }

  /**
   * Maps Offer Set criteria into a form patch.
   */
  private buildOfferSetPatch(offerSet: EligibleOfferSetModel): Record<string, unknown> {
    const patch: Record<string, unknown> = {
      year: this.resolveNumber(offerSet.year),
      make: this.resolveString(offerSet.make),
      model: this.resolveString(offerSet.model),
      trim: this.resolveString(offerSet.trim),
      maxSalePrice: this.resolveOfferSetMaxSalePrice(offerSet),
      vehicleCondition: this.normalizeCondition(offerSet.vehicleCondition),
      isPrivate: offerSet.isPrivate === true
    };

    return patch;
  }

  /**
   * Resolves the preferred available sale price from an Offer Set.
   */
  private resolveOfferSetMaxSalePrice(offerSet: EligibleOfferSetModel): number | null {
    let maxSalePrice = this.resolveNumber(offerSet.suggestedMaxSalePrice);
    if (maxSalePrice === null) {
      maxSalePrice = this.resolveNumber(offerSet.advertisedPrice);
    }
    if (maxSalePrice === null) {
      maxSalePrice = this.resolveNumber(offerSet.buyPrice);
    }

    return maxSalePrice;
  }

  /**
   * Filters active models for the selected make and enables selection.
   */
  private filterModelsForMake(makeId: number | null): void {
    let models = [this.anyModelOption, ...this.models];
    if (makeId !== null) {
      models = [this.anyModelOption, ...this.models.filter((model) => model.makeId === makeId)];
    }
    this.filteredModels = models;
    const modelControl = this.ruleForm.get('modelId');
    if (modelControl) {
      modelControl.enable({emitEvent: false});
    }
  }

  /**
   * Synchronizes make and model identifiers with stored form names.
   */
  private applyMakeAndModelSelectionFromForm(): void {
    if (this.ruleForm && this.makes.length > 0) {
      const makeName = this.getControlString('make');
      const modelName = this.getControlString('model');
      const makeId = this.findMakeIdByName(makeName);
      this.selectedMakeId = makeId;
      this.filterModelsForMake(makeId);
      const modelId = this.findModelIdByNameAndMake(modelName, makeId);
      this.ruleForm.patchValue({makeId, modelId}, {emitEvent: false});
    }
  }

  /**
   * Finds a make identifier by case-insensitive display name.
   */
  private findMakeIdByName(makeName: string | null): number | null {
    let makeId: number | null = null;
    if (makeName !== null) {
      const normalizedName = makeName.toLowerCase();
      const make = this.makes.find((option) => option.displayName.toLowerCase() === normalizedName);
      if (make && make.id !== undefined) {
        makeId = make.id;
      }
    }

    return makeId;
  }

  /**
   * Finds a model identifier by case-insensitive name and make.
   */
  private findModelIdByNameAndMake(modelName: string | null, makeId: number | null): number | null {
    let modelId: number | null = null;
    if (modelName !== null && makeId !== null) {
      const normalizedName = modelName.toLowerCase();
      const model = this.models.find((option) => option.makeId === makeId && option.name.toLowerCase() === normalizedName);
      if (model && model.id !== undefined) {
        modelId = model.id;
      }
    }

    return modelId;
  }

  /**
   * Resolves a make display name from its identifier.
   */
  private getMakeNameById(makeId: number | null): string {
    let makeName = '';
    if (makeId !== null) {
      const make = this.makes.find((option) => option.id === makeId);
      if (make) {
        makeName = make.displayName;
      }
    }

    return makeName;
  }

  /**
   * Determines the rule type from the presence of a discount amount.
   */
  private getRuleTypeFromRule(rule: OfferRuleModel): string {
    let ruleType = offerSetRuleType;
    if (this.resolveNumber(rule.dollarAmountDiscount) !== null) {
      ruleType = discountRuleType;
    }

    return ruleType;
  }

  /**
   * Checks whether the current form selects a dollar discount rule.
   */
  private isDiscountRule(): boolean {
    let isDiscount = false;
    if (this.ruleForm) {
      const ruleTypeControl = this.ruleForm.get('ruleType');
      if (ruleTypeControl) {
        isDiscount = ruleTypeControl.value === discountRuleType;
      }
    }

    return isDiscount;
  }

  /**
   * Resolves a form control value as a finite number.
   */
  private getControlNumber(controlName: string): number | null {
    let value: number | null = null;
    const control = this.ruleForm.get(controlName);
    if (control) {
      value = this.resolveNumber(control.value);
    }

    return value;
  }

  /**
   * Resolves a form control value as a trimmed non-empty string.
   */
  private getControlString(controlName: string): string | null {
    let value: string | null = null;
    const control = this.ruleForm.get(controlName);
    if (control) {
      value = this.resolveString(control.value);
    }

    return value;
  }

  /**
   * Resolves a form control value as a strict boolean.
   */
  private getControlBoolean(controlName: string): boolean {
    let value = false;
    const control = this.ruleForm.get(controlName);
    if (control) {
      value = control.value === true;
    }

    return value;
  }

  /**
   * Converts an unknown finite numeric value to a number.
   */
  private resolveNumber(value: unknown): number | null {
    let resolvedValue: number | null = null;
    if (value !== null && value !== undefined && value !== '') {
      const numberValue = Number(value);
      if (Number.isFinite(numberValue)) {
        resolvedValue = numberValue;
      }
    }

    return resolvedValue;
  }

  /**
   * Converts an unknown string value to trimmed non-empty text.
   */
  private resolveString(value: unknown): string | null {
    let resolvedValue: string | null = null;
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue !== '') {
        resolvedValue = trimmedValue;
      }
    }

    return resolvedValue;
  }

  /**
   * Maps a condition value to a supported canonical condition.
   */
  private normalizeCondition(value: unknown): string {
    let condition = 'Any';
    const stringValue = this.resolveString(value);
    if (stringValue !== null) {
      const conditionMap: {[key: string]: string} = {new: 'New', used: 'Used', cpo: 'CPO', any: 'Any'};
      const mappedCondition = conditionMap[stringValue.toLowerCase()];
      if (mappedCondition) {
        condition = mappedCondition;
      }
    }

    return condition;
  }

  /**
   * Shows a vehicle dependency loading failure.
   */
  private showLoadError(detail: string): void {
    this.messageService.add({severity: 'error', summary: 'Error', detail});
  }
}
